import { useEffect, useRef, useState } from 'react';

/**
 * Kamera, která čte QR i čárové kódy výrobců (EAN, Code 128, UPC…).
 * Android Chrome umí čárové kódy nativně (BarcodeDetector) — rychlé a úsporné.
 * Kde to prohlížeč neumí (iPhone), načte se až teď knihovna ZXing.
 * Stejný kód se nepošle dvakrát po sobě dřív než za 2,5 s (kamera vidí štítek pořád).
 * Kamera vyžaduje HTTPS nebo localhost; ruční pole funguje vždy (i pro čtečku do USB).
 */

// BarcodeDetector není v typech TS — stačí nám tohle.
interface DetectedBarcode { rawValue: string }
interface BarcodeDetectorLike { detect(source: CanvasImageSource): Promise<DetectedBarcode[]> }
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;

const FORMATS = ['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'itf', 'data_matrix'];
const REPEAT_MS = 2500;

export default function BarcodeScanner({
  onCode,
  paused = false,
  className = '',
}: {
  onCode: (code: string) => void;
  paused?: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const last = useRef<{ code: string; at: number }>({ code: '', at: 0 });

  const emit = (raw: string) => {
    const code = raw.trim();
    if (!code || pausedRef.current) return;
    const now = Date.now();
    if (code === last.current.code && now - last.current.at < REPEAT_MS) return;
    last.current = { code, at: now };
    if (navigator.vibrate) navigator.vibrate(60);
    onCodeRef.current(code);
  };

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let raf = 0;
    let zxingStop: (() => void) | null = null;

    if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
      setError('Kamera vyžaduje HTTPS (nebo localhost). Zadej kód ručně níže.');
      return;
    }

    const Native = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;

    const startNative = async (Detector: BarcodeDetectorCtor) => {
      stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const video = videoRef.current;
      if (!video || stopped) return;
      video.srcObject = stream;
      await video.play().catch(() => {});
      const detector = new Detector({ formats: FORMATS });
      const tick = async () => {
        if (stopped) return;
        if (video.readyState >= video.HAVE_ENOUGH_DATA) {
          try {
            const found = await detector.detect(video);
            if (found[0]?.rawValue) emit(found[0].rawValue);
          } catch { /* jeden snímek se nepovedl — zkusí se další */ }
        }
        raf = window.setTimeout(tick, 150);
      };
      tick();
    };

    const startZxing = async () => {
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const video = videoRef.current;
      if (!video || stopped) return;
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, video, (result) => {
        if (result) emit(result.getText());
      });
      if (stopped) controls.stop();
      else zxingStop = () => controls.stop();
    };

    (Native ? startNative(Native) : startZxing()).catch(() =>
      setError('Kameru se nepodařilo spustit (odepřený přístup?). Zadej kód ručně níže.'),
    );

    return () => {
      stopped = true;
      clearTimeout(raf);
      zxingStop?.();
      stream?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={className}>
      {!error && (
        <div className="relative overflow-hidden rounded-xl bg-black">
          <video ref={videoRef} className="aspect-[4/3] w-full object-cover" muted playsInline />
          {/* Zaměřovací rámeček — čárový kód drž vodorovně uvnitř. */}
          <div className="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-lg border-2 border-white/70" />
          {paused && <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-medium text-white">Pozastaveno</div>}
        </div>
      )}
      {error && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">{error}</p>}
      <form
        className="mt-2 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          last.current = { code: '', at: 0 }; // ruční zadání smí opakovat stejný kód
          emit(manual);
          setManual('');
        }}
      >
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          placeholder="Kód nebo název materiálu"
          className="h-11 min-w-0 flex-1 rounded-lg border border-zinc-300 bg-white px-3 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          inputMode="text"
          autoCapitalize="none"
        />
        <button type="submit" className="h-11 rounded-lg bg-zinc-900 px-4 text-sm font-medium text-white dark:bg-zinc-100 dark:text-zinc-900">OK</button>
      </form>
    </div>
  );
}
